import React, { useEffect } from 'react';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import { Formik } from 'formik';

import { connect } from 'react-redux';

import { LeaseCreatorPageFooter } from '../common/LeaseCreatorPageFooter';
import { LeaseCreatorFormItem } from '../common/LeaseCreatorFormItem';
import LeaseHeaderProgress from '../common/LeaseHeaderProgress';
import { StyledColumn, StyledContainer, StyledRow } from '../common/LeaseStyles';
import { LeaseRejectedAlert } from '../common/LeaseRejectedAlert';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';

import { Input, Switch } from '@yuhuinc/ui-library';

import { leaseCreatorColors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';
import { media, sizes } from 'helpers/MediaHelper';
import { responsive } from 'helpers/StyleHelper';
import { extractSuffix, ADDITIONAL_FIELDS } from 'helpers/DocumentTemplateHelper';

import { storeAdditionalFieldsForLease } from '../store/actions';

const StyledFormContainer = styled.div`
  background-color: white;
  width: 100%;
  margin-top: 24px;
  border: 1px solid ${leaseCreatorColors.FORM_BORDER};
  border-radius: 5px;
`;

const StyledForm = styled(VerticalFormWrapper)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  font-size: 24px;
  padding: 16px;
  max-width: 580px;
`;

const StyledPageHeader = styled.div`
  font-size: 24px;
  line-height: 32px;
  color: ${leaseCreatorColors.HEADER};
  font-weight: 400;
  margin-bottom: 16px;
  ${media.mobile`
    margin: 0;
  `};
`;

const StyledHeaderDef = styled.div`
  font-size: 14px;
  line-height: 24px;
  color: ${leaseCreatorColors.HEADER};
  font-weight: 400;
  margin-bottom: 16px;
  ${media.mobile`
    font-size: 16px;
  `};
`;

const FullWidthInput = styled(Input)`
  width: 100%;
`;

const StyledSwitch = styled(Switch)`
  border-radius: 16px !important;
  ${media.mobile`
    margin: 5px 0;
  `};
`;

const FORM_NAME = 'additional_info_page';

const AdditionalInformationPage = ({
  forward,
  back,
  currentPage,
  totalPages,
  handleCancellation,
  draftTemplateAdditionalFields,
  leaseFormValues,
  setLeaseFormValues,
  saveDraftLease,
  leaseDetails,
  selectedBaseDocumentTemplate,
  conditionalAddendumsForLease,
  fixedAddendumsForLease,
  storeAdditionalFieldsForLease,
  additionalFieldsForLease,
  latestLeaseReview,
}) => {
  useEffect(() => {
    storeAdditionalFieldsForLease(
      selectedBaseDocumentTemplate,
      conditionalAddendumsForLease,
      fixedAddendumsForLease
    );
  }, [
    storeAdditionalFieldsForLease,
    selectedBaseDocumentTemplate,
    conditionalAddendumsForLease,
    fixedAddendumsForLease,
  ]);

  const isMobile = window.matchMedia(`(max-width: ${sizes.mobile}px)`).matches;
  const labelSize = isMobile ? responsive.text.mobile : responsive.text.desktop;

  const convertStringBooleanToBoolean = (value) => value === 'true';

  let additionalFieldsToUse = additionalFieldsForLease;

  if (!isEmpty(draftTemplateAdditionalFields)) {
    const savedAdditionalFieldsIds = draftTemplateAdditionalFields.map(
      (additionalField) => additionalField.id
    );
    const availableAdditionalFieldsIds = additionalFieldsForLease.map(
      (additionalField) => additionalField.id
    );
    if (isEqual(savedAdditionalFieldsIds.sort(), availableAdditionalFieldsIds.sort())) {
      additionalFieldsToUse = draftTemplateAdditionalFields;
    }
  }

  return (
    <StyledContainer>
      <LeaseHeaderProgress lease={leaseDetails} stepsFilled={currentPage} totalSteps={totalPages} />

      <Formik
        initialValues={{
          additional_fields: additionalFieldsToUse || [],
        }}
        onSubmit={(values) => {
          setLeaseFormValues((leaseFormValues) => ({
            ...leaseFormValues,
            ...values,
          }));
          saveDraftLease(values);
          forward();
        }}
      >
        {({ handleSubmit, values, setFieldValue }) => {
          return (
            <StyledFormContainer>
              <StyledForm id={FORM_NAME} onSubmit={handleSubmit}>
                <StyledPageHeader>
                  {getTranslatedString('landlordPortal.leaseCreatorV2.additionalInfoPage')}
                </StyledPageHeader>
                {latestLeaseReview?.status === 'rejected' && (
                  <StyledRow gutter={16}>
                    <LeaseRejectedAlert latestLeaseReview={latestLeaseReview} />
                  </StyledRow>
                )}
                {values.additional_fields.length === 0 && (
                  <StyledHeaderDef>
                    {getTranslatedString('landlordPortal.leaseCreatorV2.noAdditionalInfoText')}
                  </StyledHeaderDef>
                )}

                {values.additional_fields.map((field, index) => {
                  return (
                    <StyledRow md={16}>
                      <StyledColumn md={24}>
                        {field.field_type === 'text' && (
                          <LeaseCreatorFormItem
                            label={extractSuffix(field.label, ADDITIONAL_FIELDS)}
                            labelSize={labelSize}
                          >
                            <FullWidthInput
                              value={field.default_value}
                              handleChange={(e) => {
                                setFieldValue(
                                  `additional_fields[${index}].default_value`,
                                  e.target.value
                                );
                              }}
                              isMobile={isMobile}
                            />
                          </LeaseCreatorFormItem>
                        )}

                        {field.field_type === 'checkbox' && (
                          <StyledSwitch
                            label={extractSuffix(field.label, ADDITIONAL_FIELDS)}
                            labelSize={labelSize}
                            checked={convertStringBooleanToBoolean(field.default_value)}
                            onChange={(value) => {
                              setFieldValue(
                                `additional_fields[${index}].default_value`,
                                `${value}`
                              );
                            }}
                            isMobile={isMobile}
                          />
                        )}
                      </StyledColumn>
                    </StyledRow>
                  );
                })}
              </StyledForm>
            </StyledFormContainer>
          );
        }}
      </Formik>

      <LeaseCreatorPageFooter
        back={back}
        currentPage={currentPage}
        totalPages={totalPages}
        formName={FORM_NAME}
        handleCancellation={handleCancellation}
      />
    </StyledContainer>
  );
};

const mapStoreToProps = (store) => ({
  draftTemplateAdditionalFields:
    store.landlordApplications?.leaseDetails?.draft_lease_data?.additional_fields,
  selectedBaseDocumentTemplate: store.landlord.leaseCreatorV2.selectedBaseDocumentTemplate,
  conditionalAddendumsForLease: store.landlord.leaseCreatorV2.conditionalAddendumsForLease,
  fixedAddendumsForLease: store.landlord.leaseCreatorV2.fixedAddendumsForLease,
  additionalFieldsForLease: store.landlord.leaseCreatorV2.additionalFieldsForLease,
  latestLeaseReview: store.landlord.leaseCreatorV2.latestLeaseReview,
});

const mapDispatchToProps = { storeAdditionalFieldsForLease };

export default connect(mapStoreToProps, mapDispatchToProps)(AdditionalInformationPage);
